import type { App } from "../app.js";
import { memberName } from "../app.js";
import { announce, formatBytes, formatDate, h, notice, plural, replace } from "../dom.js";
import { StudioError, type Project } from "../studio/backend.js";
import { download, exportProblems, exportSlidesZip } from "../studio/export.js";
import { projectFile, slug } from "../studio/project-file.js";
import { loadFonts, type FontStatus } from "../studio/fonts.js";
import { ImageStore, prepareImage } from "../studio/images.js";
import { followTitle, imageRefs, itemRank, LIMITS, newSlide, normalizeDoc, OBJECTIVES, setObjective, slidesFromText, type Objective, type Slide, type StudioDoc } from "../studio/model.js";
import { HEIGHT, renderSlide, WIDTH, type LoadedImage, type RenderAssets, type RenderReport } from "../studio/render.js";
import { loadMonogram } from "../studio/monogram.js";
import { COVER_CHECKS_VERSION, USE_LABEL, type Preparation } from "../prepare/model.js";
import type { Envelope } from "../prepare/scenario.js";
import { claimStates, coverFingerprint, preparedStatus, studyState, textsFingerprint } from "../prepare/status.js";
import { conceptProgress, MECHANISM_LABEL } from "../prepare/cover.js";
import { briefForCopy } from "../prepare/brief.js";
import { briefView, claimChip, claimsView, copyBriefButton, coverCheckView } from "./prepare-parts.js";
import { discardDraft, ProjectSession, readDraft, type Draft } from "../studio/session.js";
import { icon } from "../icons.js";

type Tab = "text" | "image" | "layout" | "more";
const TABS: Array<[Tab, string]> = [
  ["text", "Texte"],
  ["image", "Image"],
  ["layout", "Mise en page"],
  ["more", "Plus"],
];

const KIND_LABEL = (doc: StudioDoc, i: number) => {
  const s = doc.slides[i]!;
  return s.kind === "cover" ? "Couverture" : s.kind === "end" ? "Fin" : `Élément ${itemRank(doc, i)}`;
};
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const STATE_LABEL = { saved: "Enregistré", unsaved: "Modifications non enregistrées", saving: "Enregistrement…", error: "Erreur d'enregistrement", conflict: "Conflit de version" } as const;
const STATE_CLASS = { saved: "ok", unsaved: "warn", saving: "", error: "bad", conflict: "bad" } as const;

/** The Atelier's editor: one project of the « liste commentée » pilot. */
export function studioEditorView(app: App, id: string): HTMLElement {
  const root = h("div", { class: "st-editor" }, h("p", { class: "muted" }, "Ouverture du projet…"));
  void (async () => {
    try {
      const [project, fonts, monogram] = await Promise.all([app.connection.studio.getProject(id), loadFonts(), loadMonogram()]);
      if (project.archived_at) {
        replace(root, notice("warn", "Projet archivé", "Ce projet a été archivé : il n'est plus modifiable.", h("a", { class: "btn", href: "#/atelier" }, "Retour à l'atelier")));
        return;
      }
      const prep = normalizeDoc(project.doc).preparation;
      // the preparation screen (with its checks and validators) is loaded only when needed
      if (prep && prep.stage !== "built") new (await import("./prepare.js")).Preparer(app, root, project, fonts, monogram);
      else new Editor(app, root, project, fonts, monogram);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      replace(root, notice("bad", error instanceof StudioError && error.code === "not_found" ? "Projet introuvable" : "Ouverture impossible", message, h("a", { class: "btn", href: "#/atelier" }, "Retour à l'atelier"), h("button", { class: "btn", onclick: () => app.refresh() }, "Réessayer")));
    }
  })();
  return root;
}

class Editor {
  private readonly session: ProjectSession;
  private readonly images: ImageStore;
  private selected = 0;
  private reports: RenderReport[] = [];
  private frame = 0;
  private dirtyThumbs = new Set<number>();
  private readonly preview = h("canvas", { width: String(WIDTH), height: String(HEIGHT), class: "st-canvas", tabindex: "0", "aria-label": "Aperçu de la slide. Glisser pour recadrer l'image, ou flèches du clavier." });
  private readonly scratch = document.createElement("canvas");
  private readonly strip = h("ol", { class: "st-strip", "aria-label": "Slides, dans l'ordre" });
  private readonly panel = h("div", { class: "st-panel" });
  private readonly notes = h("div", { class: "st-notes", "aria-live": "polite" });
  private readonly banner = h("div", { class: "st-banner" });
  private readonly chip = h("span", { class: "chip", role: "status" });
  private readonly exportArea = h("div", { "aria-live": "polite" });
  /** A project built from a preparation: where it stands editorially, and its context. */
  private readonly prepStatus = h("div", { class: "pp-status", "aria-live": "polite" });
  private readonly prepTools = h("div");
  private dossierOpen = false;
  /** The settings tab shown, kept from one slide to the next. */
  private tab: Tab = "text";
  /** « Vérifications » replaces the settings in the right panel. */
  private checks = false;
  private readonly checksButton: HTMLButtonElement;
  private readonly statusBar: HTMLButtonElement;
  private blocked = 0;
  private reviewButton: HTMLButtonElement | null = null;
  private readonly nameInput: HTMLInputElement;
  private readonly sync = new Map<string, () => void>();
  private fontsOk: boolean;

  constructor(
    private readonly app: App,
    private readonly root: HTMLElement,
    project: Project,
    private fonts: FontStatus,
    private readonly monogram: LoadedImage | null,
  ) {
    this.fontsOk = fonts.ok;
    this.images = new ImageStore(app.connection.studio);
    this.session = new ProjectSession(app.connection.studio, this.images, project, { onState: () => this.showState() });
    this.scratch.width = WIDTH;
    this.scratch.height = HEIGHT;
    this.nameInput = h("input", {
      class: "st-name",
      type: "text",
      maxlength: String(LIMITS.name),
      value: project.name,
      "aria-label": "Nom du projet",
      oninput: () => {
        this.session.name = this.nameInput.value.trim() || "Sans titre";
        this.session.changed();
      },
    });
    this.checksButton = h("button", { class: "btn", type: "button", "aria-pressed": "false", onclick: () => this.toggleChecks() }, icon("shield"), h("span", null, "Vérifications"));
    this.statusBar = h("button", { class: "ed-status", type: "button", "aria-label": "Ouvrir les vérifications", onclick: () => this.toggleChecks(true) }, this.prepStatus);
    this.build();
    this.offerDraft(project);
    this.loadImages();
    app.onLeave(() => {
      void this.session.save().finally(() => this.session.dispose());
      window.removeEventListener("keydown", this.keys);
      app.setBusy(false);
    });
    window.addEventListener("keydown", this.keys);
  }

  private get doc(): StudioDoc {
    return this.session.doc;
  }
  private get slide(): Slide {
    return this.doc.slides[this.selected]!;
  }
  private get assets(): RenderAssets {
    return { images: this.images.images, monogram: this.monogram };
  }

  private readonly keys = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      void this.session.save();
    }
  };

  // -------------------------------------------------------------------------
  // Layout
  // -------------------------------------------------------------------------

  private build(): void {
    this.root.classList.add("st-studio");
    const stage = h(
      "section",
      { class: "st-stage", "aria-label": "Aperçu" },
      h("div", { class: "st-frame" }, this.preview),
      h(
        "div",
        { class: "row st-stage-tools" },
        h("button", { class: "btn", type: "button", "aria-pressed": "false", onclick: (e: Event) => this.togglePhone(e.currentTarget as HTMLButtonElement) }, icon("phone"), h("span", null, "Vue téléphone")),
        h("span", { class: "muted st-size" }, "1080 × 1350, identique à l'export"),
      ),
      this.notes,
    );
    replace(
      this.root,
      h(
        "header",
        { class: "st-bar" },
        h("a", { class: "btn icon-only", href: "#/atelier", "aria-label": "Retour aux projets" }, icon("back")),
        h("div", { class: "st-title" }, this.nameInput),
        this.chip,
        h("button", { class: "btn icon-only", type: "button", "aria-label": "Enregistrer", title: "Enregistrer (Ctrl+S)", onclick: () => void this.session.save() }, icon("save")),
        this.checksButton,
        h("button", { class: "btn primary", type: "button", onclick: () => void this.exportZip() }, icon("download"), h("span", null, "Exporter")),
      ),
      h("div", { class: "st-messages" }, this.banner, this.fontsOk ? null : this.fontProblem(), this.exportArea),
      h("div", { class: "st-grid" }, h("aside", { class: "st-side", "aria-label": "Slides" }, this.strip, this.addButtons()), stage, this.panel),
      this.statusBar,
    );
    this.wirePreview();
    this.buildStrip();
    this.buildPanel();
    this.buildPrep();
    this.showState();
    this.redraw(true);
  }

  /** The right panel shows the settings of the slide, or the checks of the project. */
  private toggleChecks(on = !this.checks): void {
    this.checks = on;
    this.checksButton.setAttribute("aria-pressed", String(on));
    this.buildPanel();
    if (on) this.panel.scrollTop = 0;
  }

  // -------------------------------------------------------------------------
  // The preparation this project comes from
  // -------------------------------------------------------------------------

  private showPrepStatus(): void {
    const st = preparedStatus(this.doc);
    const prep = this.doc.preparation;
    const blocked = this.reports.filter((r) => r?.blocking.length).length;
    const technical = blocked ? h("span", { class: "chip bad", "data-blocked": String(blocked) }, icon("image"), `${plural(blocked, "slide à corriger", "slides à corriger")}`) : null;
    if (!st || !prep) {
      replace(this.prepStatus, h("span", { class: "chips pp-chips" }, technical ?? h("span", { class: "chip ok", "data-blocked": "0" }, icon("check"), "Prêt à exporter")));
      return;
    }
    const review =
      st.review === "valid"
        ? h("span", { class: "chip ok", "data-review": "valid", title: `${memberName(this.app, prep.review!.by)}, ${formatDate(prep.review!.at)}` }, "Revue faite")
        : st.review === "outdated"
          ? h("span", { class: "chip warn", "data-review": "outdated", title: "Un texte a changé depuis la revue" }, "Revue à refaire")
          : h("span", { class: "chip", "data-review": "none" }, "Revue à faire");
    const cover = { none: ["", "Couverture à contrôler"], valid: ["ok", "Couverture contrôlée"], partial: ["warn", "Contrôle incomplet"], outdated: ["warn", "Couverture à recontrôler"] }[st.cover];
    replace(
      this.prepStatus,
      h(
        "span",
        { class: "chips pp-chips" },
        st.missingImages.length
          ? h("span", { class: "chip warn", "data-missing-images": st.missingImages.join(","), title: `Slides ${st.missingImages.join(", ")}` }, icon("image"), plural(st.missingImages.length, "image à ajouter", "images à ajouter"))
          : h("span", { class: "chip ok", "data-missing-images": "" }, icon("image"), "Images complètes"),
        h("span", { class: `chip ${st.facts.open.length || st.facts.rejected.length ? "warn" : "ok"}`, "data-facts": `${st.facts.confirmed}/${st.facts.shown}` }, `Faits ${st.facts.confirmed}/${st.facts.shown}`),
        review,
        h("span", { class: `chip ${cover[0]}`, "data-cover": st.cover }, cover[1]),
        this.studyChip(),
        technical,
      ),
    );
    if (this.reviewButton) this.reviewButton.textContent = st.review === "outdated" ? "Renouveler la revue éditoriale" : st.review === "valid" ? "Valider à nouveau la revue éditoriale" : "Valider la revue éditoriale";
  }

  /** Where the cover comparison stands; a cover built without it, or without an image, stays visible. */
  private studyChip(): HTMLElement {
    const prep = this.doc.preparation!;
    const state = studyState(prep);
    const decision = prep.cover_study?.decision;
    const image = decision ? prep.cover_study!.concepts[decision.chosen]?.image : null;
    if (state === "decided") return h("span", { class: `chip ${image ? "ok" : "warn"}`, "data-study": "decided" }, `Concept n°${decision!.chosen + 1}${image ? "" : ", sans image"}`);
    return h("span", { class: "chip", "data-study": state }, state === "started" ? "Couvertures sans choix" : "Couvertures non comparées");
  }

  /** The comparison as it was left in the preparation: the concepts, the choice and its reasons. */
  private studyView(env: Envelope | null): HTMLElement {
    const prep = this.doc.preparation!;
    const study = prep.cover_study;
    if (!study) return h("p", { class: "muted", "data-study-view": "none" }, "Les couvertures n'ont pas été comparées avant la construction. Le comparateur se trouve dans la préparation (étape 5).");
    const decision = study.decision;
    return h(
      "div",
      { "data-study-view": decision ? "decided" : "started" },
      decision
        ? h("p", null, h("strong", null, `Concept n°${decision.chosen + 1} retenu`), ` par ${memberName(this.app, decision.by)}, ${formatDate(decision.at)} : ${decision.reason}`)
        : h("p", { class: "muted" }, "Comparaison commencée, aucun concept retenu."),
      h(
        "ul",
        null,
        study.concepts.map((c) => {
          const hk = env?.hooks?.[c.hook_index];
          const p = conceptProgress(c);
          const rejected = decision?.rejected.find((r) => r.hook_index === c.hook_index && r.reason);
          return h(
            "li",
            { "data-concept": String(c.hook_index + 1) },
            h("strong", null, `Concept ${c.hook_index + 1}`),
            hk ? ` — ${hk.title}` : "",
            hk?.mechanism ? h("span", { class: "chip" }, MECHANISM_LABEL[hk.mechanism] ?? hk.mechanism) : null,
            h("span", { class: "muted" }, ` · ${c.image ? "image déposée" : "image à venir"} · ${p.answered}/${p.answerable} questions répondues`),
            c.glance ? h("div", { class: "muted" }, `Coup d'œil : ${c.glance}`) : null,
            rejected ? h("div", { class: "muted" }, `Écarté : ${rejected.reason}`) : null,
          );
        }),
      ),
    );
  }

  /** The dossier and the reference images, read-only once built: drawn when opened (it reads the library). */
  private dossierDetails(prep: Preparation): HTMLElement {
    const body = h("div");
    const fill = () => {
      if (body.childElementCount) return;
      replace(body, h("p", { class: "muted" }, "Chargement…"));
      // loaded on demand, as the preparation screen is: the editor's bundle stays the same
      void import("./prepare-dossier.js").then(
        (m) => replace(body, m.dossierTools(this.app, prep, { readOnly: true })),
        (error: unknown) => replace(body, notice("bad", "Dossier indisponible", error instanceof Error ? error.message : String(error))),
      );
    };
    const details = h(
      "details",
      {
        ontoggle: () => {
          this.dossierOpen = details.open;
          if (details.open) fill();
        },
      },
      h("summary", null, "Ouvrir le dossier et les images (lecture seule)"),
      body,
    );
    if (this.dossierOpen) {
      details.open = true;
      fill();
    }
    return details;
  }

  private buildPrep(): void {
    const prep = this.doc.preparation;
    const st = preparedStatus(this.doc);
    if (!prep || !st) {
      replace(this.prepTools);
      return;
    }
    const env = prep.envelope as unknown as Envelope | null;
    const hook = env?.hooks?.[prep.cover_hook] ?? null;
    const cover = this.doc.slides.find((sl) => sl.kind === "cover");
    const sources = env?.request?.research?.sources ?? [];
    replace(
      this.prepTools,
      h(
        "div",
        { class: "pp-context" },
        h("p", { class: "muted st-help" }, `${prep.id} · ${formatDate(prep.built_at)} · ${memberName(this.app, prep.built_by)}`),
        h(
          "section",
          { class: "pp-block", "data-part": "dossier" },
          h("h3", null, "Dossier et images de référence"),
          this.dossierDetails(prep),
        ),
        h(
          "section",
          { class: "pp-block" },
          h("h3", null, "Références"),
          prep.references.length
            ? h("ul", null, prep.references.map((r) => h("li", null, `Dépôt n°${r.number} — ${USE_LABEL[r.use]}${r.reason ? ` : ${r.reason}` : ""} (analyse ${r.analysis.state === "current" ? "à jour" : r.analysis.state === "stale" ? "périmée" : r.analysis.state === "incomplete" ? "incomplète" : "absente"} lors du choix)`)))
            : h("p", { class: "muted" }, "Aucune référence."),
          prep.inspiration_url ? h("p", { class: "muted" }, "Lien d'inspiration (non analysé par l'atelier) : ", h("a", { href: prep.inspiration_url, target: "_blank", rel: "noopener noreferrer" }, prep.inspiration_url)) : null,
        ),
        h(
          "section",
          { class: "pp-block" },
          h("h3", null, "Sources et affirmations"),
          claimsView(this.app, claimStates(prep), (review) => {
            prep.claim_reviews.push(review);
            this.session.changed();
            this.buildPrep();
            this.buildPanel();
          }, sources),
        ),
        h(
          "section",
          { class: "pp-block" },
          h("h3", null, "Revue éditoriale"),
          h("p", { class: "muted" }, "Elle porte sur les textes de toutes les slides : un texte modifié ensuite demande une nouvelle revue."),
          st.reviewBlocked ? h("p", { class: "pp-error" }, `Pas encore possible : ${st.reviewBlocked}.`) : null,
          (this.reviewButton = h(
            "button",
            {
              class: "btn",
              type: "button",
              disabled: st.reviewBlocked ? true : null,
              onclick: () => {
                prep.review = { by: this.app.member.user_id, at: new Date().toISOString(), fingerprint: textsFingerprint(this.doc) };
                this.session.changed();
                this.buildPrep();
              },
            },
            st.review === "outdated" ? "Renouveler la revue éditoriale" : "Valider la revue éditoriale",
          )),
        ),
        h(
          "section",
          { class: "pp-block" },
          h("h3", null, "Comparaison des couvertures"),
          this.studyView(env),
        ),
        h(
          "section",
          { class: "pp-block" },
          h("h3", null, "Contrôle de couverture"),
          coverCheckView(this.app, hook, cover?.title ?? "", prep.cover_check, st.cover, (items, note) => {
            prep.cover_check = { by: this.app.member.user_id, at: new Date().toISOString(), fingerprint: coverFingerprint(this.doc), items, note, version: COVER_CHECKS_VERSION };
            this.session.changed();
            this.buildPrep();
          }),
        ),
      ),
    );
    this.showPrepStatus();
  }

  private fontProblem(): HTMLElement {
    return notice(
      "bad",
      "Polices non chargées",
      `Impossible de charger : ${this.fonts.failed.join(", ")}. L'aperçu et l'export sont bloqués pour ne jamais produire de slides dans une autre police.`,
      h(
        "button",
        {
          class: "btn",
          onclick: async () => {
            this.fonts = await loadFonts();
            this.fontsOk = this.fonts.ok;
            replace(this.exportArea);
            this.build();
          },
        },
        "Réessayer",
      ),
    );
  }

  private togglePhone(button: HTMLButtonElement): void {
    const on = this.root.classList.toggle("st-phone");
    button.setAttribute("aria-pressed", String(on));
    button.lastElementChild!.textContent = on ? "Vue large" : "Vue téléphone";
  }

  private addButtons(): HTMLElement {
    return h(
      "div",
      { class: "st-add" },
      h("button", { class: "btn", type: "button", "aria-label": "Ajouter un élément", onclick: () => this.addItem() }, icon("plus"), h("span", null, "Ajouter")),
    );
  }

  // -------------------------------------------------------------------------
  // Save state, drafts, conflicts
  // -------------------------------------------------------------------------

  private showState(): void {
    const s = this.session;
    this.chip.className = `chip ${STATE_CLASS[s.state]}`;
    let label: string = STATE_LABEL[s.state];
    if (s.state === "saved") label += ` · v${s.version}, ${memberName(this.app, s.updatedBy)}, ${formatDate(s.updatedAt)}`;
    if (s.state === "error") label += s.nextRetry ? ` — nouvel essai dans ${Math.max(1, Math.round((s.nextRetry - Date.now()) / 1000))} s` : "";
    if (!s.draftOk && s.state !== "saved") label += " · brouillon local impossible";
    this.chip.textContent = label;
    this.chip.title = s.message;
    this.app.setBusy(s.state !== "saved");
    this.showPrepStatus();
    if (s.state === "error") replace(this.banner, notice("bad", "Enregistrement impossible pour l'instant", `${s.message} Tes modifications restent dans cet onglet${s.draftOk ? " et dans ce navigateur" : ""}.`, h("button", { class: "btn", onclick: () => void s.save() }, "Réessayer maintenant")));
    else if (s.state === "conflict") this.showConflict();
    else if (this.banner.dataset["kind"] !== "draft") replace(this.banner);
  }

  private showConflict(): void {
    const s = this.session;
    this.banner.dataset["kind"] = "conflict";
    replace(
      this.banner,
      notice(
        "warn",
        "Version modifiée par ailleurs",
        h(
          "p",
          null,
          `L'autre membre a enregistré ce projet${s.remoteVersion ? ` (version ${s.remoteVersion})` : ""} pendant tes modifications. Rien n'a été écrasé : tes modifications sont toujours là. Choisis quoi en faire.`,
        ),
        h("button", { class: "btn primary", onclick: () => void this.saveAsCopy() }, "Enregistrer ma version comme nouveau projet"),
        h("button", { class: "btn", onclick: () => void this.downloadProject() }, "Télécharger ma version (fichier de projet)"),
        h(
          "button",
          {
            class: "btn",
            onclick: async () => {
              if (!window.confirm("Ouvrir la version enregistrée ? Tes modifications non enregistrées seront abandonnées.")) return;
              await s.reload();
              this.afterReload();
            },
          },
          "Ouvrir la version enregistrée",
        ),
      ),
    );
  }

  private afterReload(): void {
    delete this.banner.dataset["kind"];
    replace(this.banner);
    this.nameInput.value = this.session.name;
    this.selected = Math.min(this.selected, this.doc.slides.length - 1);
    this.loadImages();
    this.buildStrip();
    this.buildPanel();
    this.buildPrep();
    this.redraw(true);
  }

  private async saveAsCopy(): Promise<void> {
    try {
      await this.images.flush();
      const copy = await this.app.connection.studio.createProject(`${this.session.name} (ma version)`.slice(0, LIMITS.name), this.doc);
      this.session.forgetLocalChanges();
      this.app.navigate(`#/atelier/${copy.id}`);
    } catch (error) {
      replace(this.exportArea, notice("bad", "Copie impossible", error instanceof Error ? error.message : String(error)));
    }
  }

  private async downloadProject(doc: StudioDoc = this.doc, name = this.session.name): Promise<void> {
    try {
      const blob = await projectFile(name, doc, (sha) => this.imageBlob(sha), { id: this.session.id, version: this.session.version });
      download(blob, `projet-${slug(name)}.zip`);
    } catch (error) {
      replace(this.exportArea, notice("bad", "Fichier de projet impossible", error instanceof Error ? error.message : String(error)));
    }
  }

  private async imageBlob(sha: string): Promise<Blob> {
    const known = this.images.blob(sha);
    if (known) return known;
    return this.app.connection.studio.getImage(sha);
  }

  private offerDraft(project: Project): void {
    const draft = readDraft(project.id);
    if (!draft) return;
    const when = formatDate(draft.at);
    this.banner.dataset["kind"] = "draft";
    const close = () => {
      delete this.banner.dataset["kind"];
      replace(this.banner);
    };
    if (draft.baseVersion === project.version) {
      replace(
        this.banner,
        notice(
          "info",
          "Modifications non enregistrées dans ce navigateur",
          `Des modifications du ${when} n'avaient pas été enregistrées (coupure ou fermeture). Elles partent de la version actuelle.`,
          h("button", { class: "btn primary", onclick: () => (this.session.restore(draft), close(), this.afterRestore()) }, "Les reprendre"),
          h("button", { class: "btn", onclick: () => (discardDraft(project.id), close()) }, "Les abandonner"),
        ),
      );
      return;
    }
    replace(
      this.banner,
      notice(
        "warn",
        "Brouillon local plus ancien que le projet",
        `Ce navigateur garde des modifications du ${when} faites sur la version ${draft.baseVersion}. Le projet a été enregistré depuis (version ${project.version}, ${memberName(this.app, project.updated_by)}). Pour ne rien perdre :`,
        h("button", { class: "btn", onclick: () => void this.draftAsCopy(draft).then(close) }, "Ouvrir le brouillon comme nouveau projet"),
        h("button", { class: "btn", onclick: () => void this.downloadProject(this.docOf(draft), draft.name) }, "Télécharger le brouillon"),
        h("button", { class: "btn", onclick: () => window.confirm("Abandonner ce brouillon local ?") && (discardDraft(project.id), close()) }, "L'abandonner"),
      ),
    );
  }

  private docOf(draft: Draft): StudioDoc {
    return { ...this.doc, ...(draft.doc as StudioDoc) };
  }

  private async draftAsCopy(draft: Draft): Promise<void> {
    try {
      const copy = await this.app.connection.studio.createProject(`${draft.name} (brouillon)`.slice(0, LIMITS.name), draft.doc);
      discardDraft(this.session.id);
      this.app.navigate(`#/atelier/${copy.id}`);
    } catch (error) {
      replace(this.exportArea, notice("bad", "Copie impossible", error instanceof Error ? error.message : String(error)));
    }
  }

  private afterRestore(): void {
    this.nameInput.value = this.session.name;
    this.selected = 0;
    this.loadImages();
    this.buildStrip();
    this.buildPanel();
    this.buildPrep();
    this.redraw(true);
  }

  // -------------------------------------------------------------------------
  // Images
  // -------------------------------------------------------------------------

  private loadImages(): void {
    for (const ref of imageRefs(this.doc)) {
      if (this.images.images.has(ref.sha256)) continue;
      this.images.load(ref).then(
        () => this.redraw(true),
        (error) => replace(this.exportArea, notice("bad", "Image non chargée", error instanceof Error ? error.message : String(error))),
      );
    }
  }

  private async importImages(files: File[], start = this.selected): Promise<void> {
    if (!files.length) return;
    replace(this.exportArea, h("p", { class: "muted" }, `Préparation de ${plural(files.length, "image", "images")}…`));
    const errors: string[] = [];
    let index = start;
    for (const file of files) {
      if (index >= this.doc.slides.length) break;
      try {
        const prepared = await prepareImage(file);
        this.images.add(prepared);
        const slide = this.doc.slides[index]!;
        // as the generator does on import: no darkening, full bottom fade, arrow shown
        Object.assign(slide, { image: prepared.ref, fx: 50, fy: 50, zoom: 1, darken: 0, fade: 1 });
        if (slide.kind !== "end") slide.arrow = true;
        this.session.changed();
      } catch (error) {
        errors.push(`${file.name} : ${error instanceof Error ? error.message : String(error)}`);
      }
      index += 1;
    }
    replace(this.exportArea, errors.length ? notice("bad", "Certaines images n'ont pas été importées", h("ul", null, errors.map((e) => h("li", null, e)))) : null);
    this.tab = "image";
    this.buildPanel();
    this.redraw(true);
  }

  // -------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------

  /** Redraws on the next frame: the selected slide, and the thumbnails touched (all after a structural change). */
  private redraw(all = false): void {
    if (all) this.doc.slides.forEach((_, i) => this.dirtyThumbs.add(i));
    else this.dirtyThumbs.add(this.selected);
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      if (!this.fontsOk) return;
      const ctx = this.preview.getContext("2d")!;
      const report = renderSlide(ctx, this.doc, this.selected, this.assets);
      this.reports[this.selected] = report;
      this.showNotes(report);
      for (const i of this.dirtyThumbs) {
        if (i >= this.doc.slides.length) continue;
        let source: HTMLCanvasElement = this.preview;
        if (i !== this.selected) {
          this.reports[i] = renderSlide(this.scratch.getContext("2d")!, this.doc, i, this.assets);
          source = this.scratch;
        }
        const thumb = this.strip.querySelector<HTMLCanvasElement>(`canvas[data-index="${i}"]`);
        thumb?.getContext("2d")!.drawImage(source, 0, 0, thumb.width, thumb.height);
        this.strip.querySelector(`[data-flag="${i}"]`)?.toggleAttribute("hidden", !this.reports[i]?.blocking.length);
      }
      this.dirtyThumbs.clear();
      const blocked = this.reports.filter((r) => r?.blocking.length).length;
      if (blocked !== this.blocked) {
        this.blocked = blocked;
        this.showPrepStatus();
        if (this.checks) this.buildPanel();
      }
    });
  }

  private showNotes(report: RenderReport): void {
    replace(
      this.notes,
      report.blocking.length ? notice("bad", "À corriger avant l'export", h("ul", null, report.blocking.map((b) => h("li", null, b)))) : null,
      report.warnings.length ? h("p", { class: "muted st-note" }, report.warnings.join(" ")) : null,
    );
  }

  // -------------------------------------------------------------------------
  // Editing
  // -------------------------------------------------------------------------

  private edit(fn: (slide: Slide, doc: StudioDoc) => void, structural = false): void {
    fn(this.slide, this.doc);
    this.session.changed();
    this.redraw(structural);
  }

  private select(index: number): void {
    this.selected = clamp(index, 0, this.doc.slides.length - 1);
    for (const li of this.strip.children) li.toggleAttribute("aria-current", Number((li as HTMLElement).dataset["index"]) === this.selected);
    // picking a slide brings its settings back
    this.checks = false;
    this.checksButton.setAttribute("aria-pressed", "false");
    this.buildPanel();
    this.redraw();
  }

  private addItem(): void {
    if (this.doc.slides.length >= LIMITS.slides) {
      replace(this.exportArea, notice("warn", "Limite atteinte", `${LIMITS.slides} slides au plus par carrousel (limite d'Instagram).`));
      return;
    }
    const end = this.doc.slides.findIndex((s) => s.kind === "end");
    const at = this.slide.kind === "item" ? this.selected + 1 : end >= 0 ? end : this.doc.slides.length;
    this.doc.slides.splice(at, 0, newSlide("item", { title: "NOUVEL ÉLÉMENT", accent: ["NOUVEL"] }));
    this.session.changed();
    this.selected = at;
    this.buildStrip();
    this.buildPanel();
    this.redraw(true);
    announce(`Élément ajouté en position ${at + 1}.`);
  }

  private move(index: number, delta: number): void {
    const target = index + delta;
    const slides = this.doc.slides;
    if (slides[index]?.kind !== "item" || slides[target]?.kind !== "item") return;
    [slides[index], slides[target]] = [slides[target]!, slides[index]!];
    this.session.changed();
    this.selected = target;
    this.buildStrip();
    this.buildPanel();
    this.redraw(true);
    announce(`Slide déplacée en position ${target + 1}.`);
    (this.strip.querySelector(`[data-move="${target}:${delta}"]`) as HTMLElement | null)?.focus();
  }

  private duplicate(index: number): void {
    if (this.doc.slides.length >= LIMITS.slides) return;
    const copy = { ...structuredClone(this.doc.slides[index]!), id: newSlide("item").id };
    this.doc.slides.splice(index + 1, 0, copy);
    this.session.changed();
    this.selected = index + 1;
    this.buildStrip();
    this.buildPanel();
    this.redraw(true);
  }

  private remove(index: number): void {
    const items = this.doc.slides.filter((s) => s.kind === "item").length;
    if (this.doc.slides[index]?.kind !== "item" || items <= 1) return;
    if (!window.confirm(`Supprimer la slide ${index + 1} ?`)) return;
    this.doc.slides.splice(index, 1);
    this.session.changed();
    this.selected = Math.min(index, this.doc.slides.length - 1);
    this.buildStrip();
    this.buildPanel();
    this.redraw(true);
    announce("Slide supprimée.");
  }

  // -------------------------------------------------------------------------
  // The strip of slides
  // -------------------------------------------------------------------------

  private buildStrip(): void {
    const doc = this.doc;
    const items = doc.slides.filter((s) => s.kind === "item").length;
    replace(
      this.strip,
      doc.slides.map((s, i) => {
        const isItem = s.kind === "item";
        return h(
          "li",
          { "data-index": String(i), "aria-current": i === this.selected ? "true" : null },
          h(
            "button",
            { class: "st-pick", type: "button", onclick: () => this.select(i), "aria-label": `Slide ${i + 1} : ${KIND_LABEL(doc, i)}` },
            h("canvas", { width: "216", height: "270", "data-index": String(i), "aria-hidden": "true" }),
            h("span", { class: "st-num" }, String(i + 1).padStart(2, "0")),
            h("span", { class: "st-kind" }, KIND_LABEL(doc, i)),
            h("span", { class: "st-flag", "data-flag": String(i), hidden: true, title: "À corriger avant l'export" }, "!"),
          ),
          isItem
            ? h(
                "div",
                { class: "st-tools" },
                h("button", { class: "icon-btn", type: "button", "data-move": `${i}:-1`, disabled: doc.slides[i - 1]?.kind !== "item", onclick: () => this.move(i, -1), "aria-label": `Monter la slide ${i + 1}` }, icon("up")),
                h("button", { class: "icon-btn", type: "button", "data-move": `${i}:1`, disabled: doc.slides[i + 1]?.kind !== "item", onclick: () => this.move(i, 1), "aria-label": `Descendre la slide ${i + 1}` }, icon("down")),
                h("button", { class: "icon-btn", type: "button", disabled: doc.slides.length >= LIMITS.slides, onclick: () => this.duplicate(i), "aria-label": `Dupliquer la slide ${i + 1}` }, icon("copy")),
                h("button", { class: "icon-btn", type: "button", disabled: items <= 1, onclick: () => this.remove(i), "aria-label": `Supprimer la slide ${i + 1}` }, icon("close")),
              )
            : null,
        );
      }),
    );
  }

  private pasteBox(): HTMLElement {
    const area = h("textarea", { rows: "8", placeholder: "Titre de la couverture\n\nPremier élément\nSon explication\n\nDeuxième élément\nSon explication", "aria-label": "Texte à transformer en slides" });
    const apply = (mode: "replace" | "append") => {
      const { cover, items } = slidesFromText(area.value);
      if (!cover && !items.length) return;
      const slides = this.doc.slides;
      const current = slides.filter((s) => s.kind === "item");
      if (mode === "replace" && current.length && !window.confirm(`Remplacer les ${plural(current.length, "élément", "éléments")} actuels par ceux du texte ?`)) return;
      const room = LIMITS.slides - (mode === "replace" ? slides.length - current.length : slides.length);
      const created = items.slice(0, Math.max(0, room)).map((it) => newSlide("item", it));
      const coverSlide = slides.find((s) => s.kind === "cover");
      if (cover && coverSlide) Object.assign(coverSlide, cover);
      const end = slides.filter((s) => s.kind === "end");
      const rest = slides.filter((s) => s.kind === "cover");
      this.doc.slides = mode === "replace" ? [...rest, ...created, ...end] : [...rest, ...current, ...created, ...end];
      this.session.changed();
      area.value = "";
      this.selected = 0;
      this.buildStrip();
      this.buildPanel();
      this.redraw(true);
      announce(`${plural(created.length, "élément créé", "éléments créés")} à partir du texte.`);
    };
    return h(
      "details",
      { class: "st-paste" },
      h("summary", null, "Coller un texte"),
      h("p", { class: "muted" }, "Un paragraphe par slide, séparés par une ligne vide. La première ligne de chaque paragraphe devient le titre, la suite l'explication. Un premier paragraphe d'une seule ligne devient la couverture. *mot* = mot surligné (sinon, le dernier mot)."),
      area,
      h("div", { class: "row" }, h("button", { class: "btn primary", type: "button", onclick: () => apply("replace") }, "Remplacer les éléments"), h("button", { class: "btn", type: "button", onclick: () => apply("append") }, "Ajouter à la suite")),
    );
  }

  // -------------------------------------------------------------------------
  // Preview: crop by dragging, keyboard, image drop
  // -------------------------------------------------------------------------

  private wirePreview(): void {
    const canvas = this.preview;
    let start: { x: number; y: number; fx: number; fy: number } | null = null;
    const coverSize = () => {
      const ref = this.slide.image;
      const img = ref ? this.images.images.get(ref.sha256) : undefined;
      if (!img) return null;
      const s = Math.max(WIDTH / img.width, HEIGHT / img.height) * this.slide.zoom;
      return { w: img.width * s, h: img.height * s };
    };
    const moveBy = (dx: number, dy: number, from: { fx: number; fy: number }) => {
      const size = coverSize();
      if (!size) return;
      // the image's left edge is at fx % × (1080 − its width): moving it by dx changes fx accordingly
      this.edit((s) => {
        if (size.w - WIDTH > 1) s.fx = clamp(from.fx + (dx * 100) / (WIDTH - size.w), 0, 100);
        if (size.h - HEIGHT > 1) s.fy = clamp(from.fy + (dy * 100) / (HEIGHT - size.h), 0, 100);
      });
      this.sync.get("fx")?.();
      this.sync.get("fy")?.();
    };
    canvas.addEventListener("pointerdown", (e) => {
      if (!this.slide.image) return;
      canvas.setPointerCapture(e.pointerId);
      start = { x: e.clientX, y: e.clientY, fx: this.slide.fx, fy: this.slide.fy };
    });
    canvas.addEventListener("pointermove", (e) => {
      if (!start) return;
      const scale = WIDTH / canvas.clientWidth;
      moveBy((e.clientX - start.x) * scale, (e.clientY - start.y) * scale, start);
    });
    const stop = () => (start = null);
    canvas.addEventListener("pointerup", stop);
    canvas.addEventListener("pointercancel", stop);
    canvas.addEventListener("keydown", (e) => {
      const step = e.shiftKey ? 60 : 15;
      const d = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] }[e.key];
      if (!d || !this.slide.image) return;
      e.preventDefault();
      moveBy(d[0]!, d[1]!, { fx: this.slide.fx, fy: this.slide.fy });
    });
    const frame = canvas.parentElement!;
    frame.addEventListener("dragover", (e) => {
      if (e.dataTransfer?.types.includes("Files")) {
        e.preventDefault();
        frame.classList.add("over");
      }
    });
    frame.addEventListener("dragleave", () => frame.classList.remove("over"));
    frame.addEventListener("drop", (e) => {
      e.preventDefault();
      frame.classList.remove("over");
      void this.importImages([...(e.dataTransfer?.files ?? [])].filter((f) => f.type.startsWith("image/")));
    });
  }

  // -------------------------------------------------------------------------
  // Settings panel
  // -------------------------------------------------------------------------

  private range(key: string, label: string, min: number, max: number, step: number, get: () => number, set: (v: number) => void, format: (v: number) => string): HTMLElement {
    const value = h("output", null, format(get()));
    const input = h("input", {
      type: "range",
      min: String(min),
      max: String(max),
      step: String(step),
      value: String(get()),
      "aria-label": label,
      oninput: () => {
        this.edit(() => set(Number(input.value)));
        value.textContent = format(Number(input.value));
      },
    });
    this.sync.set(key, () => {
      input.value = String(get());
      value.textContent = format(get());
    });
    return h("label", { class: "st-range" }, h("span", null, label, value), input);
  }

  private choice<T extends string>(label: string, options: Array<[T, string]>, get: () => T, set: (v: T) => void, structural = false): HTMLElement {
    const name = `c${Math.random().toString(36).slice(2, 8)}`;
    return h(
      "fieldset",
      { class: "st-choice" },
      h("legend", null, label),
      options.map(([value, text]) =>
        h("label", null, h("input", { type: "radio", name, value, checked: get() === value, onchange: () => (this.edit(() => set(value), structural), this.buildPanelIfNeeded(structural)) }), h("span", null, text)),
      ),
    );
  }

  private buildPanelIfNeeded(structural: boolean): void {
    if (structural) this.buildPanel();
  }

  private check(label: string, get: () => boolean, set: (v: boolean) => void, structural = false): HTMLElement {
    return h("label", { class: "st-check" }, h("input", { type: "checkbox", checked: get(), onchange: (e: Event) => this.edit(() => set((e.target as HTMLInputElement).checked), structural) }), h("span", null, label));
  }

  private buildPanel(): void {
    this.sync.clear();
    const s = this.slide;
    const i = this.selected;
    const title = h("textarea", {
      rows: "3",
      maxlength: String(LIMITS.title),
      "aria-label": "Titre",
      oninput: () => {
        this.edit((sl) => {
          sl.title = title.value;
          followTitle(sl);
        });
        this.buildWords(words);
      },
    });
    title.value = s.title;
    const sub = h("textarea", { rows: "3", maxlength: String(LIMITS.sub), "aria-label": "Texte secondaire", oninput: () => this.edit((sl) => (sl.sub = sub.value)) });
    sub.value = s.sub;
    const words = h("div", { class: "st-words" });
    this.buildWords(words);
    const other = h("input", { type: "text", maxlength: "60", placeholder: "Autre expression du titre", "aria-label": "Autre expression à surligner" });
    const file = h("input", { type: "file", accept: "image/jpeg,image/png,image/webp", class: "sr-only", onchange: () => void this.importImages([...(file.files ?? [])].slice(0, 1)).then(() => (file.value = "")) });
    const many = h("input", { type: "file", accept: "image/jpeg,image/png,image/webp", multiple: true, class: "sr-only", onchange: () => void this.importImages([...(many.files ?? [])]).then(() => (many.value = "")) });

    const tabs = h(
      "div",
      { class: "tabs st-tabs", role: "tablist", "aria-label": "Réglages" },
      TABS.map(([value, label]) =>
        h("button", { type: "button", role: "tab", "aria-selected": String(value === this.tab), onclick: () => ((this.tab = value), this.buildPanel()) }, label),
      ),
    );
    const sections: Record<Tab, Array<HTMLElement | null>> = {
      text: [
        s.kind === "end" ? this.objectivePanel(s) : null,
        h("section", { class: "st-section" }, h("label", { class: "field" }, "Titre", title), h("label", { class: "field" }, "Texte secondaire", sub)),
        h(
          "section",
          { class: "st-section" },
          h("h3", null, "Surlignage"),
          words,
          h(
            "form",
            {
              class: "row st-other",
              onsubmit: (e: Event) => {
                e.preventDefault();
                const v = other.value.trim();
                if (!v || s.accent.length >= LIMITS.accentWords) return;
                this.edit((sl) => (sl.accent = [...sl.accent, v]));
                other.value = "";
                this.buildWords(words);
              },
            },
            other,
            h("button", { class: "btn", type: "submit" }, "Ajouter"),
          ),
          this.choice("Style", [["block", "Bloc ocre"], ["letters", "Lettres ocre"]], () => s.highlight, (v) => (s.highlight = v)),
        ),
      ],
      image: [
        h(
          "section",
          { class: "st-section" },
          h(
            "div",
            { class: "row" },
            h("label", { class: "btn" }, file, icon("image"), h("span", null, s.image ? "Remplacer" : "Choisir une image")),
            s.image ? h("button", { class: "btn quiet", type: "button", onclick: () => (this.edit((sl) => (sl.image = null)), this.buildPanel()) }, "Retirer") : null,
          ),
          s.image
            ? [
                h("p", { class: "muted st-help" }, `${s.image.width} × ${s.image.height} px · glisse l'aperçu pour recadrer`),
                this.range("zoom", "Zoom", 1, 3, 0.01, () => s.zoom, (v) => (s.zoom = v), (v) => `${Math.round(v * 100)} %`),
                this.range("fx", "Cadrage horizontal", 0, 100, 1, () => s.fx, (v) => (s.fx = v), (v) => `${Math.round(v)} %`),
                this.range("fy", "Cadrage vertical", 0, 100, 1, () => s.fy, (v) => (s.fy = v), (v) => `${Math.round(v)} %`),
                this.range("darken", "Assombrir l'image", 0, 0.8, 0.01, () => s.darken, (v) => (s.darken = v), (v) => `${Math.round(v * 100)} %`),
                this.range("fade", "Dégradé de lisibilité (bas)", 0, 1, 0.01, () => s.fade, (v) => (s.fade = v), (v) => `${Math.round(v * 100)} %`),
                this.range("fadeStrength", "Intensité du dégradé", 0, 1, 0.01, () => s.fadeStrength, (v) => (s.fadeStrength = v), (v) => `${Math.round(v * 100)} %`),
              ]
            : h("p", { class: "muted st-help" }, "Ou dépose une image sur l'aperçu."),
          h("label", { class: "btn quiet" }, many, "Plusieurs images, une par slide à partir d'ici"),
        ),
        this.slidePrep(s),
      ],
      layout: [
        h(
          "section",
          { class: "st-section" },
          this.choice("Alignement", [["left", "Gauche"], ["center", "Centre"], ["right", "Droite"]], () => s.align, (v) => (s.align = v)),
          this.choice("Position du texte", [["top", "Haut"], ["center", "Milieu"], ["bottom", "Bas"]], () => s.position, (v) => (s.position = v)),
          this.range("titleSize", "Taille du titre", 28, 200, 2, () => s.titleSize ?? 100, (v) => (s.titleSize = v), (v) => `${v} px`),
          this.range("subSize", "Taille du texte secondaire", 24, 60, 1, () => s.subSize ?? 35, (v) => (s.subSize = v), (v) => `${v} px`),
          s.kind !== "end" ? this.check(s.kind === "cover" ? "Flèche cerclée" : "Petite flèche", () => s.arrow, (v) => (s.arrow = v)) : null,
        ),
      ],
      more: [
        h(
          "section",
          { class: "st-section" },
          h("h3", null, "Carrousel"),
          this.check("Barre de progression sur les éléments", () => this.doc.settings.progress, (v) => (this.doc.settings.progress = v), true),
          this.check("Éléments numérotés (numéro cerclé)", () => this.doc.settings.numbered, (v) => (this.doc.settings.numbered = v), true),
        ),
        h("section", { class: "st-section" }, this.pasteBox()),
        h(
          "section",
          { class: "st-section" },
          h("h3", null, "Projet"),
          h("div", { class: "row" }, h("button", { class: "btn", type: "button", onclick: () => void this.downloadProject() }, icon("download"), h("span", null, "Fichier de projet")), h("button", { class: "btn quiet", type: "button", onclick: () => void this.archive() }, "Archiver")),
          h("p", { class: "muted st-help" }, "Textes, réglages et images dans un .zip, réimportable depuis l'Atelier."),
        ),
      ],
    };

    if (this.checks) {
      const blocked = this.reports.map((r, n) => [n, r?.blocking ?? []] as const).filter(([, b]) => b.length);
      replace(
        this.panel,
        h("div", { class: "st-panel-head" }, h("h2", null, "Vérifications"), h("button", { class: "btn icon-only", type: "button", "aria-label": "Fermer les vérifications", onclick: () => this.toggleChecks(false) }, icon("close"))),
        h(
          "section",
          { class: "st-section", "data-technical": String(blocked.length) },
          h("h3", null, "Export"),
          blocked.length
            ? h("ul", { class: "st-blocked" }, blocked.map(([n, b]) => h("li", null, h("button", { class: "link", type: "button", onclick: () => this.select(n) }, `Slide ${n + 1}`), ` : ${b.join(" ")}`)))
            : h("p", { class: "muted st-help" }, "Rien ne bloque l'export."),
          h("p", { class: "muted st-help" }, "L'export PNG est technique : il ne vaut pas validation éditoriale."),
        ),
        this.prepTools,
      );
      return;
    }
    replace(this.panel, h("h2", { class: "st-panel-title" }, `Slide ${i + 1} · ${KIND_LABEL(this.doc, i)}`), tabs, h("div", { role: "tabpanel", class: "st-tabpanel" }, sections[this.tab]));
  }

  /** The scenario slide this one comes from: its brief and its facts (kept while images are added). */
  private slidePrep(s: Slide): HTMLElement | null {
    const prep = this.doc.preparation;
    if (!prep || prep.stage !== "built") return null;
    const link = prep.slide_links.find((l) => l.slide_id === s.id);
    if (!link) return h("section", { class: "st-section pp-slide" }, h("h3", null, "Préparation"), h("p", { class: "muted st-help" }, "Slide ajoutée après la construction : aucun brief ni fait associé."));
    const states = new Map(claimStates(prep).map((c) => [c.claim.id, c]));
    return h(
      "section",
      { class: "st-section pp-slide" },
      h("h3", null, `Brief de la slide ${link.outline_index + 1} du scénario (${link.role})`),
      link.brief && !s.image ? h("p", { class: "chip warn" }, "Image attendue, pas encore posée") : null,
      briefView(link.brief),
      copyBriefButton(() => briefForCopy({ preparationId: prep.id, slide: this.doc.slides.indexOf(s) + 1, role: link.role, title: s.title, sub: s.sub, brief: link.brief }), this.doc.slides.indexOf(s) + 1),
      link.claim_ids.length ? h("div", { class: "row" }, link.claim_ids.map((id) => [h("span", null, id), states.get(id) ? claimChip(states.get(id)!) : h("span", { class: "chip bad" }, "inconnue")])) : null,
    );
  }

  private objectivePanel(s: Slide): HTMLElement {
    const handle = h("input", { type: "text", maxlength: "40", value: s.handle ?? "", "aria-label": "Compte affiché sur la carte", oninput: () => this.edit((sl) => (sl.handle = handle.value.replace(/^@/, "").trim() || "opulanciamedia")) });
    const button = h("input", { type: "text", maxlength: "40", value: s.button ?? "", "aria-label": "Texte du bouton", oninput: () => this.edit((sl) => (sl.button = button.value)) });
    return h(
      "section",
      { class: "st-section" },
      h("h3", null, "Objectif de la fin"),
      h("p", { class: "muted st-help" }, "Un seul appel à la fois. Changer d'objectif remplace les textes de la fin."),
      this.choice<Objective>(
        "Objectif",
        (Object.keys(OBJECTIVES) as Objective[]).map((o) => [o, OBJECTIVES[o].label]),
        () => s.objective ?? "follow",
        (v) => setObjective(s, v),
        true,
      ),
      s.objective === "follow" ? h("label", { class: "field" }, "Compte sur la carte d'abonnement", handle) : null,
      s.objective === "save" ? h("label", { class: "field" }, "Texte du bouton", button) : null,
    );
  }

  /** The title's words as toggles, then the expressions highlighted that are not single words. */
  private buildWords(box: HTMLElement): void {
    const s = this.slide;
    const upper = (w: string) => w.toLocaleUpperCase("fr-FR");
    const words = [...new Set(s.title.split(/\s+/).map((w) => w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "")).filter(Boolean).map(upper))];
    const on = new Set(s.accent.map(upper));
    const extra = s.accent.filter((a) => !words.includes(upper(a)));
    replace(
      box,
      words.map((w) =>
        h(
          "button",
          {
            type: "button",
            class: `st-word${on.has(w) ? " on" : ""}`,
            "aria-pressed": String(on.has(w)),
            onclick: () => {
              this.edit((sl) => (sl.accent = on.has(w) ? sl.accent.filter((a) => upper(a) !== w) : [...sl.accent, w].slice(0, LIMITS.accentWords)));
              this.buildWords(box);
            },
          },
          w,
        ),
      ),
      extra.map((a) =>
        h("button", { type: "button", class: "st-word on", "aria-label": `Retirer le surlignage de ${a}`, onclick: () => (this.edit((sl) => (sl.accent = sl.accent.filter((x) => x !== a))), this.buildWords(box)) }, `${a} ×`),
      ),
    );
  }

  private async archive(): Promise<void> {
    if (!window.confirm("Archiver ce projet ? Il disparaît de la liste pour les deux membres (il n'est pas effacé de la base).")) return;
    try {
      if (!(await this.session.save())) throw new Error("Enregistre d'abord les modifications en cours.");
      await this.app.connection.studio.archiveProject(this.session.id, this.session.version);
      discardDraft(this.session.id);
      this.app.navigate("#/atelier");
    } catch (error) {
      replace(this.exportArea, notice("bad", "Archivage impossible", error instanceof Error ? error.message : String(error)));
    }
  }

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------

  /** After a PNG export of a prepared project: the export is technical, the editorial state is said apart. */
  private editorialNote(): HTMLElement | null {
    const st = preparedStatus(this.doc);
    if (!st) return null;
    const missing = [
      st.facts.open.length ? `${st.facts.open.length} fait(s) à vérifier` : "",
      st.facts.rejected.length ? `${st.facts.rejected.length} fait(s) rejeté(s)` : "",
      st.review !== "valid" ? (st.review === "outdated" ? "revue éditoriale à renouveler" : "revue éditoriale non faite") : "",
      st.cover !== "valid" ? "contrôle de couverture non complet" : "",
      st.missingImages.length ? `images attendues manquantes (slides ${st.missingImages.join(", ")})` : "",
    ].filter(Boolean);
    return missing.length
      ? notice("warn", "Export technique seulement", `Validation éditoriale incomplète : ${missing.join(" ; ")}.`)
      : notice("ok", "Validation éditoriale complète", "Faits confirmés, revue et contrôle de couverture à jour. La publication reste une décision humaine.");
  }

  private async exportZip(): Promise<void> {
    if (!this.fontsOk) {
      replace(this.exportArea, this.fontProblem());
      return;
    }
    replace(this.exportArea, h("p", { class: "muted" }, "Préparation de l'export…"));
    try {
      await Promise.all(imageRefs(this.doc).map((ref) => this.images.load(ref)));
      const problems = exportProblems(this.doc, this.assets);
      if (problems.length) {
        replace(this.exportArea, notice("bad", "Export bloqué", h("ul", null, problems.map((p) => h("li", null, p)))));
        this.redraw(true);
        return;
      }
      const result = await exportSlidesZip(this.doc, this.assets);
      const name = `${slug(this.session.name)}.zip`;
      download(result.zip, name);
      const bytes = result.zip.size;
      replace(
        this.exportArea,
        h(
          "div",
          { "data-export-ms": String(Math.round(result.ms)), "data-export-bytes": String(bytes) },
          notice("ok", "ZIP exporté", `${name} : ${plural(result.files.length, "slide", "slides")} (${result.files[0]!.name} à ${result.files.at(-1)!.name}, couverture en premier), ${formatBytes(bytes)}, rendu en ${(result.ms / 1000).toFixed(1)} s.`),
          this.editorialNote(),
        ),
      );
    } catch (error) {
      replace(this.exportArea, notice("bad", "Export impossible", error instanceof Error ? error.message : String(error)));
    }
  }
}
